import { Link } from "react-router-dom";
import { formatFCFA, effectivePrice, hasPromo, discountPercent, isLowStock } from "@/lib/format";
import { Wrench, Package, Star, Flame, Clock } from "lucide-react";
import PromoCountdown from "@/components/PromoCountdown";

export default function ProductCard({ p, rating }) {
  const img = (p.images || [])[0];
  const isNew = p.created_at && (Date.now() - new Date(p.created_at).getTime()) < 30 * 24 * 3600 * 1000;
  return (
    <Link to={`/produit/${p.id}`} data-testid={`product-card-${p.id}`}
      className="group bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm hover:shadow-md hover:border-[#E08B00]/40 hover:-translate-y-0.5 transition-all">
      <div className="aspect-square bg-slate-100 overflow-hidden relative">
        {img ? (
          <img src={img} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-slate-300"><Package className="w-10 h-10" /></div>
        )}
        <div className="absolute top-2 left-2 flex flex-col gap-1 items-start">
          {(p.is_pack || p.installation_type !== "none") && (
            <span className="bg-[#0F172A] text-white text-[10px] font-semibold px-2 py-0.5 rounded-full flex items-center gap-1">
              <Wrench className="w-3 h-3" /> {p.is_pack ? "Pack + pose" : "Pose dispo"}
            </span>
          )}
          {isNew && (
            <span className="bg-blue-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-full" data-testid={`new-badge-${p.id}`}>Nouveau</span>
          )}
        </div>
        {isLowStock(p) && (
          <span className="absolute top-2 right-2 bg-amber-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">Plus que {p.stock} {p.unit}</span>
        )}
        {p.stock <= 0 && (
          <span className="absolute top-2 right-2 bg-slate-700 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">Rupture</span>
        )}
        {hasPromo(p) && (
          <span className="absolute bottom-2 left-2 inline-flex items-center gap-1 bg-gradient-to-r from-[#DC2626] to-[#F97316] text-white text-xs font-extrabold px-2.5 py-1 rounded-lg shadow-md" data-testid={`promo-badge-${p.id}`}>
            <Flame className="w-3.5 h-3.5" /> -{discountPercent(p)}%
          </span>
        )}
      </div>
      <div className="p-3">
        {p.brand && <p className="text-[11px] text-slate-400 uppercase tracking-wide font-semibold">{p.brand}</p>}
        <h3 className="text-sm font-semibold text-slate-800 line-clamp-2 leading-snug min-h-[2.5rem]">{p.name}</h3>
        {rating && rating.count > 0 && (
          <div className="mt-1 flex items-center gap-1" data-testid={`card-rating-${p.id}`}>
            <Star className="w-3.5 h-3.5 fill-[#E08B00] text-[#E08B00]" />
            <span className="text-xs font-semibold text-slate-700 font-num">{rating.average}</span>
            <span className="text-[11px] text-slate-400">({rating.count})</span>
          </div>
        )}
        <div className="mt-1.5 flex items-baseline gap-1.5 flex-wrap">
          <span className="font-num font-bold text-[#C85A32] text-base">{formatFCFA(effectivePrice(p))}</span>
          {hasPromo(p) && <span className="font-num text-[11px] text-slate-400 line-through">{formatFCFA(p.price)}</span>}
          <span className="text-[11px] text-slate-400">/ {p.unit}</span>
        </div>
        {hasPromo(p) && p.promo_end && (
          <div className="mt-1.5 inline-flex items-center gap-1 bg-red-50 text-[#DC2626] text-[10px] font-bold px-2 py-1 rounded-md w-fit" data-testid={`promo-timer-${p.id}`}>
            <Clock className="w-3 h-3" /> Fin dans <PromoCountdown end={p.promo_end} compact />
          </div>
        )}
      </div>
    </Link>
  );
}
